import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight, FiImage } from 'react-icons/fi';
import API_BASE_URL from '../../config/api';

const GalleryPreview = () => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchImages = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/manage_gallery.php?limit=5`);
                const data = await response.json();
                if (data.status === 'success') {
                    setImages(data.data);
                }
            } catch (error) {
                console.error("Error fetching gallery images:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchImages();
    }, []);

    if (loading) return null;
    if (images.length === 0) return null;

    return (
        <section className="py-10 relative overflow-hidden bg-transparent">
            <div className="max-w-6xl mx-auto px-6 relative z-10">
                <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
                    <div className="max-w-xl text-center md:text-left">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="flex items-center justify-center md:justify-start gap-2 text-rose-600 font-semibold mb-2 tracking-wider uppercase text-xs"
                        >
                            <span className="w-8 h-[2px] bg-rose-600"></span>
                            Visual Journey
                        </motion.div>
                        <motion.h2 
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.1 }}
                            className="text-3xl md:text-4xl font-bold text-slate-900 mb-3"
                        >
                            Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-600 to-orange-500">Gallery</span>
                        </motion.h2>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2 }}
                            className="text-sm text-slate-600 leading-relaxed"
                        >
                            Explore our vibrant campus and state-of-the-art facilities.
                        </motion.p>
                    </div>
                    
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                    >
                        <Link 
                            to="/gallery" 
                            className="group flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-rose-600 transition-all duration-300 shadow-lg"
                        >
                            View More
                            <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </motion.div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 h-[250px] md:h-[200px]">
                    {images.map((img, index) => (
                        <motion.div
                            key={img.id}
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1 }}
                            className="relative group overflow-hidden rounded-xl shadow-md h-full"
                        >
                            <img 
                                src={`${API_BASE_URL}/../${img.image_path}`} 
                                alt={img.caption}
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-3">
                                <p className="text-white font-medium text-[10px] md:text-xs transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                                    {img.caption || 'Campus View'}
                                </p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default GalleryPreview;
